/**
 * Search Keyboard Shortcuts Hook
 * Handles global keyboard shortcuts for global search
 */

import { useEffect, useState, useCallback } from 'react';

/**
 * Hook to enable global search keyboard shortcuts
 * Cmd+Shift+F (Mac) or Ctrl+Shift+F (Windows/Linux) - Open global search
 */
export function useSearchKeyboard() {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
  }, []);

  const toggleSearch = useCallback(() => {
    setIsSearchOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if event was already handled by another handler
      if (event.defaultPrevented) return;

      const isModKey = event.metaKey || event.ctrlKey;

      // Cmd+Shift+F (Mac) or Ctrl+Shift+F (Windows/Linux) - Toggle Global Search
      if (isModKey && event.shiftKey && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        toggleSearch();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleSearch]);

  return {
    isSearchOpen,
    setIsSearchOpen,
    openSearch,
    closeSearch,
    toggleSearch,
  };
}
